import type { ScoreStatus } from "@/lib/mock/exam-grading"
import type { GradeStatus, QuestionType } from "@/lib/mock/exam-grading"

/**
 * Question types as an author names them, for the Adjust Key type filter and
 * the item-analysis panel. The stored ids are kebab-case; nothing user-facing
 * should show them.
 */
export const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  "single-select": "Single Select",
  "multi-select": "Multi Select",
  "true-false": "True or False",
  "fill-blank": "Fill in the Blank",
  matching: "Matching",
  numeric: "Numeric",
  hotspot: "Hotspot",
  essay: "Essay",
  "short-answer": "Short Answer",
  "text-response": "Text Response",
}

/**
 * User-facing labels for auto-derived score workflow states.
 *
 * These map onto `SubmissionScoringStatus` in `lib/exam-status-badges`, which is
 * the vocabulary the hub Status column uses. One submission must not be called
 * "Needs scoring" on its detail page and "Unscored" in the row that links to it.
 */
export const SCORE_STATUS_LABEL: Record<ScoreStatus, string> = {
  Ungraded: "Unscored",
  "Partially Evaluated": "Partially scored",
  Graded: "Scored",
  "Scores Released": "Score Released",
}

export const SCORE_STATUS_TOOLTIP: Record<ScoreStatus, string> = {
  Ungraded: "No items scored yet.",
  "Partially Evaluated":
    "Some items are scored; others still need points.",
  Graded: "All items scored. Release scores when students should see point totals.",
  "Scores Released": "Students can view their score for this submission.",
}

/** Hub score column before any scored total exists. */
export const EVALUATION_NOT_STARTED_LABEL = "Not started"

/** Tooltip for the partial completion ring in the Score column. */
export const PARTIAL_SCORE_TOOLTIP =
  "Some items are scored; others still need points."

/** Hub grade status — each state has a distinct label (matches dot color). */
export const GRADE_STATUS_HUB_LABEL: Record<GradeStatus, string> = {
  "Not Set": "No grade",
  Set: "Ready to release",
  "Grades Released": "Released",
}

export const GRADE_STATUS_TOOLTIP: Record<GradeStatus, string> = {
  "Not Set": "Enter a letter grade in the Grade column.",
  Set: "Grade saved. Release grades when students should see it.",
  "Grades Released": "Students can view their grade for this submission.",
}

export function scoreStatusLabel(status: ScoreStatus): string {
  return SCORE_STATUS_LABEL[status]
}

export function scoreStatusTooltip(status: ScoreStatus): string {
  return SCORE_STATUS_TOOLTIP[status]
}

export function gradeStatusHubLabel(status: GradeStatus): string {
  return GRADE_STATUS_HUB_LABEL[status]
}

export function gradeStatusTooltip(status: GradeStatus): string {
  return GRADE_STATUS_TOOLTIP[status]
}

export const GRADING_ACTION_LABEL = {
  release: "Release",
  releaseScores: "Release scores",
  adjustKey: "Adjust key",
} as const

/** Faculty-facing rule: point entry stays internal until Release scores. */
export const SCORING_DOES_NOT_NOTIFY_UNTIL_RELEASED =
  "Scoring does not notify the student unless scores are released." as const

export function scoringDoesNotNotifyUntilReleased(
  audience: "student" | "students" = "student",
): string {
  return audience === "students"
    ? "Scoring does not notify students unless scores are released."
    : SCORING_DOES_NOT_NOTIFY_UNTIL_RELEASED
}

export function scoringResumeButtonLabel(kind: "continue" | "next"): string {
  return kind === "continue" ? "Resume scoring" : "Start scoring"
}

/** Submissions hub KPI strip — scoring progress and score distribution. */
export const SCORING_KPI_LABEL = {
  pending: "Pending scoring",
  average: "Average score",
  range: "Score range",
  /* "Progress" names the dimension the percent measures, and "Manual" scopes it
     to hand-scored work so it is not read as overall completion: objective
     questions auto-score, so the whole-assessment number is always higher. */
  manualScoring: "Manual scoring progress",
} as const

/** Submission detail sidebar — one submission's scoring snapshot. */
export const SUBMISSION_DETAIL_KPI_LABEL = {
  total: "Submission total",
  scoringProgress: "Scoring progress",
  classScoreProgress: "Class score progress",
  classAverage: "Class average",
} as const

export const SUBMISSION_DETAIL_KPI_SECTION = {
  student: "Student",
  class: "Class",
} as const

/** Submissions hub KPI strip — letter grade workflow. */
export const GRADING_KPI_LABEL = {
  noGrade: "No grade",
  ready: "Grades to release",
  released: "Grades released",
  set: "Grades set",
} as const

export function gradingKpiDescription(
  tile: keyof typeof GRADING_KPI_LABEL,
  ctx: {
    activeCount: number
    readyCount: number
    noGradeCount: number
    hasGrades: boolean
  },
): string {
  switch (tile) {
    case "noGrade":
      return ctx.noGradeCount > 0
        ? "scored submissions without a letter grade"
        : `of ${ctx.activeCount} submissions`
    case "ready":
      return ctx.readyCount > 0
        ? "grades saved, not visible to students"
        : `of ${ctx.activeCount} submissions`
    case "released":
      return "students can view letter grades"
    case "set":
      return ctx.hasGrades ? "letter grades entered" : "none entered yet"
  }
}
